"use client";

import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { toast } from "sonner";
import { CORE_COLORS, CORE_FONT } from "@/lib/theme/core";
import { fieldErrors, resetPasswordSchema } from "@/lib/validation/auth";
import { PasswordField } from "@/components/auth/password-field";
import { PasswordHints } from "@/components/auth/field-error";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}


export function ChangePasswordModal({ open, onOpenChange }: Props) {
  const [current, setCurrent] = React.useState("");
  const [next, setNext] = React.useState("");
  const [confirm, setConfirm] = React.useState("");
  const [showCurrent, setShowCurrent] = React.useState(false);
  const [showNext, setShowNext] = React.useState(false);
  const [showConfirm, setShowConfirm] = React.useState(false);
  const [errors, setErrors] = React.useState<Record<string, string>>({});

  const reset = () => {
    setCurrent(""); setNext(""); setConfirm(""); setErrors({});
  };

  /**
   * The modal used to hold each field's value inside its own local
   * `PasswordField`, so the parent could not read them and 確認更改 just closed
   * the dialog. QA filed it as "密碼驗證及提示". It now uses the controlled
   * `components/auth/password-field.tsx` and the same zod rules as the auth
   * pages, so the two surfaces cannot drift apart.
   */
  const handleSubmit = () => {
    const result = resetPasswordSchema.safeParse({
      password: next,
      confirmPassword: confirm,
    });
    const next_errors: Record<string, string> = result.success
      ? {}
      : fieldErrors(result.error);
    if (!current.trim()) next_errors.current = "請輸入現有密碼";
    if (Object.keys(next_errors).length > 0) {
      setErrors(next_errors);
      return;
    }
    // TODO(api): submit the change once an account endpoint exists.
    toast.success("密碼已更新");
    reset();
    onOpenChange(false);
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-[2px]" />
        <DialogPrimitive.Content
          className="fixed left-1/2 top-1/2 z-50 flex w-[calc(100vw-32px)] max-w-[440px] -translate-x-1/2 -translate-y-1/2 flex-col gap-[24px] bg-white p-[32px] shadow-xl"
          style={{ borderRadius: "3px" }}
        >
          <DialogPrimitive.Title
            style={{
              fontFamily: CORE_FONT.condensed,
              fontSize: "24px",
              fontWeight: 700,
              color: CORE_COLORS.textPrimary,
              lineHeight: 1.1,
              letterSpacing: "-0.2px",
            }}
          >
            更改密碼
          </DialogPrimitive.Title>

          <div className="flex w-full flex-col gap-[16px]">
            <PasswordField
              id="current-password"
              label="現有密碼"
              autoComplete="current-password"
              value={current}
              onChange={setCurrent}
              show={showCurrent}
              onToggle={() => setShowCurrent((v) => !v)}
              error={errors.current}
            />
            <PasswordField
              id="new-password"
              label="新密碼"
              autoComplete="new-password"
              value={next}
              onChange={setNext}
              show={showNext}
              onToggle={() => setShowNext((v) => !v)}
              error={errors.password}
              hints={<PasswordHints value={next} />}
            />
            <PasswordField
              id="confirm-new-password"
              label="確認新密碼"
              autoComplete="new-password"
              value={confirm}
              onChange={setConfirm}
              show={showConfirm}
              onToggle={() => setShowConfirm((v) => !v)}
              error={errors.confirmPassword}
            />
          </div>

          <button
            type="button"
            onClick={handleSubmit}
            className="inline-flex w-full items-center justify-center transition-colors hover:bg-[color:var(--green-dark)]"
            style={
              {
                fontFamily: CORE_FONT.sans,
                fontSize: "15px",
                fontWeight: 700,
                color: "white",
                lineHeight: "20px",
                padding: "14px 20px",
                backgroundColor: CORE_COLORS.brandGreen,
                ["--green-dark" as string]: CORE_COLORS.brandGreenDark,
                borderRadius: "3px",
              } as React.CSSProperties
            }
          >
            確認更改
          </button>

          <DialogPrimitive.Close
            aria-label="關閉"
            className="absolute right-[16px] top-[16px] inline-flex size-[28px] items-center justify-center transition-colors hover:bg-black/[0.05]"
            style={{
              border: `1px solid rgba(0,0,0,0.15)`,
              borderRadius: "999px",
            }}
          >
            <X
              className="size-[14px]"
              strokeWidth={2}
              color={CORE_COLORS.textPrimary}
            />
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
